import { Component, inject, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Textarea } from 'primeng/textarea';
import { DatePicker } from 'primeng/datepicker';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DocumentUploaderComponent } from '@workspace-app/shared';
import { getEndOfLastYear, normalizeDateToEndOfDayUTC } from '@workspace-app/shared/utils/date.utils';

export interface TransitionDialogData {
  systemName: string;
  approvalNumber: string;
  currentState: string;
  trigger: string;
  targetState: string;
  requiresDate?: boolean;
  requiresReason?: boolean;
  requiresCancellationDate?: boolean; // Show cancellation date picker with "End of last year" button
  dateLabel?: string;
  showOptionalFields?: boolean; // Show optional comment for promotion triggers
  showDocumentUpload?: boolean; // Show document upload section (defaults to true)
}

export interface TransitionDialogResult {
  confirmed: boolean;
  date?: Date;
  cancellationDate?: Date; // Cancellation date (normalized to end of day UTC)
  reason?: string;
  comment?: string; // Optional comment for promotions
  files?: File[]; // Files to upload with the transition
}

@Component({
  selector: 'app-transition-dialog',
  standalone: true,
  imports: [FormsModule, ButtonModule, Textarea, DatePicker, DocumentUploaderComponent],
  template: `
    <div class="transition-dialog">
      <div class="mb-4">
        <p class="text-lg mb-2">
          You are about to transition <strong>{{ data.approvalNumber }}</strong> from
          <span class="state-badge state-{{ data.currentState.toLowerCase() }}">{{ data.currentState }}</span>
          to
          <span class="state-badge state-{{ data.targetState.toLowerCase() }}">{{ data.targetState }}</span>
        </p>
        <p class="text-color-secondary">System: {{ data.systemName }}</p>
      </div>

      @if (data.requiresDate) {
        <div class="field mb-4">
          <label for="transitionDate" class="font-medium mb-2 block">{{ data.dateLabel || 'Date' }}</label>
          <p-datepicker
            id="transitionDate"
            [(ngModel)]="transitionDate"
            [showIcon]="true"
            dateFormat="dd/mm/yy"
            [maxDate]="today"
            appendTo="body"
            styleClass="w-full">
          </p-datepicker>
        </div>
      }

      @if (data.requiresReason) {
        <div class="field mb-4">
          <label for="reason" class="font-medium mb-2 block">Reason <span class="text-red-500">*</span></label>
          <textarea
            id="reason"
            pTextarea
            [(ngModel)]="reason"
            rows="3"
            class="w-full"
            placeholder="Enter a reason for this action...">
          </textarea>
        </div>
      }

      @if (data.requiresCancellationDate) {
        <div class="field mb-4">
          <label for="cancellationDate" class="font-medium mb-2 block">Cancellation Date</label>
          <div class="flex gap-2 align-items-center">
            <p-datepicker
              id="cancellationDate"
              [(ngModel)]="cancellationDate"
              [showIcon]="true"
              dateFormat="dd/mm/yy"
              appendTo="body"
              styleClass="flex-grow-1">
            </p-datepicker>
            <p-button
              label="End of last year"
              severity="secondary"
              [outlined]="true"
              size="small"
              (onClick)="setEndOfLastYear()">
            </p-button>
          </div>
          <small class="text-color-secondary mt-1 block">The date from which the system will no longer be valid</small>
        </div>
      }

      @if (data.showOptionalFields) {
        <div class="field mb-4">
          <label for="comment" class="font-medium mb-2 block">Comment (optional)</label>
          <textarea
            id="comment"
            pTextarea
            [(ngModel)]="comment"
            rows="3"
            class="w-full"
            placeholder="Add any notes or comments about this promotion...">
          </textarea>
        </div>
      }

      @if (data.showDocumentUpload !== false) {
        <div class="field mb-4">
          <label class="font-medium mb-2 block">Supporting Documents (optional)</label>
          <app-document-uploader [(files)]="selectedFiles" [multiple]="true"> </app-document-uploader>
        </div>
      }

      <div class="flex justify-content-end gap-2 mt-4">
        <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="onCancel()"> </p-button>
        <p-button label="Confirm" severity="primary" [disabled]="!isValid()" (onClick)="onConfirm()"> </p-button>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      /* Ensure datepicker overlay appears above dialog */
      ::ng-deep .p-datepicker-panel {
        z-index: var(--z-dialog) !important;
      }

      .transition-dialog {
        min-width: 300px;
      }

      .state-badge {
        display: inline-block;
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
        font-weight: 500;
        font-size: 0.875rem;
      }

      .state-granting {
        background-color: var(--surface-200);
        color: var(--text-color-secondary);
      }

      .state-provisional {
        background-color: var(--yellow-100);
        color: var(--yellow-700);
      }

      .state-interim {
        background-color: var(--blue-100);
        color: var(--blue-700);
      }

      .state-final {
        background-color: var(--green-100);
        color: var(--green-700);
      }

      .state-cancelled {
        background-color: var(--red-100);
        color: var(--red-700);
      }
    `,
  ],
})
export class TransitionDialogComponent {
  private ref = inject(DynamicDialogRef);
  private config = inject(DynamicDialogConfig);

  data: TransitionDialogData;
  transitionDate: Date = new Date();
  cancellationDate: Date;
  reason: string = '';
  comment: string = '';
  selectedFiles: File[] = [];
  today = new Date();

  constructor() {
    this.data = this.config.data;
    // Default cancellation date to end of last year
    this.cancellationDate = getEndOfLastYear();
  }

  setEndOfLastYear(): void {
    this.cancellationDate = getEndOfLastYear();
  }

  isValid(): boolean {
    if (this.data.requiresDate && !this.transitionDate) {
      return false;
    }
    if (this.data.requiresCancellationDate && !this.cancellationDate) {
      return false;
    }
    if (this.data.requiresReason && !this.reason.trim()) {
      return false;
    }
    return true;
  }

  onConfirm(): void {
    const result: TransitionDialogResult = {
      confirmed: true,
      date: this.transitionDate,
      cancellationDate: this.data.requiresCancellationDate
        ? normalizeDateToEndOfDayUTC(this.cancellationDate)
        : undefined,
      reason: this.reason.trim() || undefined,
      comment: this.comment.trim() || undefined,
      files: this.selectedFiles.length > 0 ? this.selectedFiles : undefined,
    };
    this.ref.close(result);
  }

  onCancel(): void {
    this.ref.close({ confirmed: false });
  }
}
